import { useState, useRef, useEffect } from 'react'
import { AudioBarsVisualizer } from './audio-bars-visualizer'


interface VoiceWidgetProps {
  isActive: boolean
  isSpeaking: boolean
  onStop: () => void
  onCancel: () => void
  demoMinutesRemaining?: number
  isDemo?: boolean
  getInputByteFrequencyData?: () => Uint8Array | undefined
  getOutputByteFrequencyData?: () => Uint8Array | undefined
}

export function VoiceWidget({ isActive, isSpeaking, onStop, onCancel, demoMinutesRemaining, isDemo, getInputByteFrequencyData, getOutputByteFrequencyData }: VoiceWidgetProps) {
  const WIDGET_WIDTH = 380
  const [position, setPosition] = useState({ x: 0, y: 100 })
  const [isDragging, setIsDragging] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [callDuration, setCallDuration] = useState(0)
  const dragRef = useRef<HTMLDivElement>(null)
  const dragStartPos = useRef({ x: 0, y: 0 })
  const startTimeRef = useRef<number | null>(null)
  
  // Always dark — floating overlay, light theme ruins waveform visibility
  const currentTheme = 'dark' as 'light' | 'dark'
  
  // Detect mobile on mount and resize, and calculate initial position
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth
      const isMobileView = width < 768 // md breakpoint
      setIsMobile(isMobileView)
      
      // Only set position on desktop
      if (!isMobileView) {
        // Center the widget horizontally, ensuring it fits within viewport
        const padding = 20 // Minimum padding from edges
        const maxX = Math.max(0, width - WIDGET_WIDTH - padding)
        const centerX = Math.max(padding, Math.min((width - WIDGET_WIDTH) / 2, maxX))
        setPosition(prev => ({ ...prev, x: centerX }))
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Track call duration
  useEffect(() => {
    if (isActive) {
      startTimeRef.current = Date.now()
      setCallDuration(0)
      
      const interval = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
          setCallDuration(elapsed)
        }
      }, 1000)
      
      return () => clearInterval(interval)
    } else {
      startTimeRef.current = null
      setCallDuration(0)
    }
  }, [isActive])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      
      const deltaX = e.clientX - dragStartPos.current.x
      const deltaY = e.clientY - dragStartPos.current.y
      
      setPosition(prev => {
        // Calculate new position
        let newX = prev.x + deltaX
        let newY = prev.y + deltaY
        
        // Keep widget within viewport bounds
        const padding = 20
        const maxX = window.innerWidth - WIDGET_WIDTH - padding
        const maxY = window.innerHeight - 200 // Approximate widget height
        
        newX = Math.max(padding, Math.min(newX, maxX))
        newY = Math.max(padding, Math.min(newY, maxY))
        
        return { x: newX, y: newY }
      })
      
      dragStartPos.current = { x: e.clientX, y: e.clientY }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't allow dragging on mobile
    if (isMobile) return
    
    // Only drag from the widget body, not buttons
    if ((e.target as HTMLElement).closest('button')) return
    
    setIsDragging(true)
    dragStartPos.current = { x: e.clientX, y: e.clientY }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive) return
      
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault()
        onStop()
      } else if (e.code === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, onStop, onCancel])

  if (!isActive) return null
  
  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  
  // Calculate remaining minutes for demo
  const calculateRemainingMinutes = () => {
    if (!isDemo || demoMinutesRemaining === undefined) return null
    const minutesUsed = Math.ceil(callDuration / 60)
    const remaining = Math.max(0, demoMinutesRemaining - minutesUsed)
    return remaining
  }
  
  const remainingMinutes = calculateRemainingMinutes()

  // Theme-aware colors while keeping black background
  const colors = currentTheme === 'dark' ? {
    // Dark theme: use purple accent matching the theme
    statusDot: isSpeaking ? 'bg-purple-500 animate-pulse' : 'bg-zinc-600',
    statusText: 'text-zinc-400',
    buttonText: 'text-zinc-300 hover:text-zinc-100',
    kbdBg: 'bg-zinc-900/70 border-zinc-700',
    border: 'border-zinc-800'
  } : {
    // Light theme: use primary accent from theme
    statusDot: isSpeaking ? 'bg-purple-600 animate-pulse' : 'bg-gray-500',
    statusText: 'text-gray-400',
    buttonText: 'text-gray-300 hover:text-white',
    kbdBg: 'bg-gray-800/50 border-gray-700',
    border: 'border-gray-700'
  }

  // Glow/shadow effect based on theme
  const shadowStyle = currentTheme === 'dark' 
    ? {
        // Dark mode: subtle light glow
        boxShadow: `
          0 0 40px rgba(255, 255, 255, 0.08),
          0 0 80px rgba(255, 255, 255, 0.04),
          0 20px 25px -5px rgba(0, 0, 0, 0.5),
          0 10px 10px -5px rgba(0, 0, 0, 0.4)
        `
      }
    : {
        // Light mode: strong dark shadow
        boxShadow: `
          0 0 60px rgba(0, 0, 0, 0.3),
          0 25px 50px -12px rgba(0, 0, 0, 0.5),
          0 10px 15px -5px rgba(0, 0, 0, 0.4)
        `
      }

  return (
    <div
      ref={dragRef}
      className={`fixed z-[100] bg-zinc-950/80 backdrop-blur-xl border ${colors.border} ${
        isMobile ? 'bottom-0 left-0 right-0 rounded-t-3xl' : 'rounded-3xl'
      }`}
      style={isMobile ? {
        width: '100%',
        ...shadowStyle
      } : {
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${WIDGET_WIDTH}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        ...shadowStyle
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="p-3">
        <div className="rounded-xl overflow-hidden mb-2 h-12 flex items-center">
          <AudioBarsVisualizer 
            isActive={isActive} 
            isSpeaking={isSpeaking}
            theme={currentTheme}
            className="w-full h-full"
            getInputByteFrequencyData={getInputByteFrequencyData}
            getOutputByteFrequencyData={getOutputByteFrequencyData}
          />
        </div>

        <div className={`flex items-center justify-between ${colors.statusText}`}>
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${colors.statusDot}`} />
            <span className="text-xs">
              {isSpeaking ? 'Listening...' : 'Ready'}
            </span>
            
            {isDemo && (
              <>
                <span className="text-[10px] opacity-50">•</span>
                <span className="text-[10px] font-mono tabular-nums">
                  {formatDuration(callDuration)}
                </span>
                {remainingMinutes !== null && remainingMinutes > 0 && (
                  <>
                    <span className="text-[10px] opacity-50">/</span>
                    <span className={`text-[10px] font-medium font-mono tabular-nums ${remainingMinutes <= 1 ? 'text-orange-400' : ''}`}>
                      {remainingMinutes}m
                    </span>
                  </>
                )}
                {remainingMinutes !== null && remainingMinutes <= 0 && (
                  <span className="text-[10px] font-medium text-orange-400 animate-pulse">
                    Hurry up!
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onStop}
              className={`flex items-center gap-1 transition-colors ${colors.buttonText}`}
            >
              <span className="text-xs">Stop</span>
              <kbd className={`hidden sm:inline-block px-1.5 py-px text-[10px] rounded border ${colors.kbdBg}`}>⌃Space</kbd>
            </button>
            
            <button
              onClick={onCancel}
              className={`flex items-center gap-1 transition-colors ${colors.buttonText}`}
            >
              <span className="text-xs">Cancel</span>
              <kbd className={`hidden sm:inline-block px-1.5 py-px text-[10px] rounded border ${colors.kbdBg}`}>Esc</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}